// Command palette — registry types + pure helpers.
//
// Per .forge/plans/v2-command-palette.md.
// Hand-rolled exception per feedback_deterministic_codegen_over_hand_rolling.md:
// commands.tsx mirrors specs/seeds/dashboard-v2/command-palette.yaml until
// the v2 FE generator emits the registry directly.

export type CommandGroup =
  | "pages"
  | "actions"
  | "donors"
  | "campaigns"
  | "recent";

export const GROUP_LABELS: Record<CommandGroup, string> = {
  pages: "Pages",
  actions: "Actions",
  donors: "Donors",
  campaigns: "Campaigns",
  recent: "Recent",
};

// Render order for groups in the palette result list.
export const GROUP_ORDER: CommandGroup[] = [
  "recent",
  "pages",
  "actions",
  "donors",
  "campaigns",
];

export interface CommandEntry {
  id: string;
  label: string;
  group: CommandGroup;
  icon?: string;
  keywords?: string[];
  route?: string;
  onSelect?: () => void;
  /** Static fallback flag — used for commands without a `route`
   *  (e.g., the "actions" group). For commands WITH a route, the
   *  effective flag is computed live via `isCommandForwardPromised`
   *  against the current nav state per CHG-1662 / ITER-3771; this
   *  static value is ignored for routed commands so the palette
   *  doesn't fall out of date when a page ships. */
  forwardPromised?: boolean;
  shortcut?: string;
}

// CHG-1662 / ITER-3771 — live SOON-flag lookup. Previously commands
// were tagged forwardPromised at bake time, so even after a page
// shipped (its nav.ts entry lost the flag) the palette kept showing
// the SOON badge. Field-test v3 reported this stale-flag bug on
// /donors, /campaigns, /designations, /donations, /pledges,
// /recurring, /settings — all of which were live.
//
// Resolution: at palette render time, look up the command's route
// against the current nav-tree items. If a matching nav item exists
// and has forwardPromised=false (or undefined), the page is live and
// the badge is suppressed. If the nav item is forwardPromised=true,
// the badge renders. If no nav item matches the route at all (e.g.,
// the route is genuinely placeholder copy in commands.tsx with no
// shell entry), fall back to the command's static `forwardPromised`
// flag — that's the only case where the palette can't derive the
// truth from nav.
export interface NavLookupItem {
  route: string;
  forwardPromised?: boolean;
}

export function isCommandForwardPromised(
  entry: CommandEntry,
  navItems: NavLookupItem[],
): boolean {
  if (!entry.route) {
    // No route — pure action command (record-donation, send-broadcast).
    // Trust the static flag because there's no nav.ts entry to consult.
    return !!entry.forwardPromised;
  }
  const navMatch = navItems.find((it) => it.route === entry.route);
  if (navMatch) {
    return !!navMatch.forwardPromised;
  }
  // Route doesn't appear in nav (e.g., /reports, /messaging — palette
  // routes that haven't been added to the sidebar yet). Fall back to
  // the entry's static flag.
  return !!entry.forwardPromised;
}

/** Flatten the nav group tree into a route→forwardPromised lookup
 *  list. Used by CommandPalette.tsx to feed isCommandForwardPromised. */
export function navItemsForLookup(
  navGroups: Array<{ items: NavLookupItem[] }>,
): NavLookupItem[] {
  const out: NavLookupItem[] = [];
  for (const g of navGroups) {
    for (const it of g.items) {
      out.push({ route: it.route, forwardPromised: it.forwardPromised });
    }
  }
  return out;
}

export interface ScoredCommand {
  entry: CommandEntry;
  score: number;
}

const normalize = (s: string): string => s.toLowerCase().trim();

// Lightweight substring scorer:
// - exact label match    → 100
// - label startsWith q   → 80
// - label includes q     → 60
// - keyword includes q   → 40
// - id includes q        → 20
// - else                 → 0 (filtered out)
//
// Returns 1 for empty query so all entries pass through with stable order.
function scoreEntry(entry: CommandEntry, q: string): number {
  if (!q) return 1;
  const label = normalize(entry.label);
  if (label === q) return 100;
  if (label.startsWith(q)) return 80;
  if (label.includes(q)) return 60;
  for (const kw of entry.keywords ?? []) {
    if (normalize(kw).includes(q)) return 40;
  }
  if (entry.id.toLowerCase().includes(q)) return 20;
  return 0;
}

export function filterCommands(
  entries: CommandEntry[],
  query: string,
): ScoredCommand[] {
  const q = normalize(query);
  const scored: ScoredCommand[] = [];
  for (const entry of entries) {
    const score = scoreEntry(entry, q);
    if (score > 0) scored.push({ entry, score });
  }
  // Stable sort: higher score first, then preserve original order.
  scored.sort((a, b) => b.score - a.score);
  return scored;
}

export type GroupedCommands = Array<{
  group: CommandGroup;
  entries: CommandEntry[];
}>;

export function groupCommands(scored: ScoredCommand[]): GroupedCommands {
  const buckets = new Map<CommandGroup, CommandEntry[]>();
  for (const { entry } of scored) {
    const bucket = buckets.get(entry.group) ?? [];
    bucket.push(entry);
    buckets.set(entry.group, bucket);
  }
  const out: GroupedCommands = [];
  for (const group of GROUP_ORDER) {
    const entries = buckets.get(group);
    if (entries && entries.length > 0) out.push({ group, entries });
  }
  return out;
}

// Returns a flat ordered list of entries as they appear in the grouped view
// — used by the component to map highlightIndex → entry.
export function flatten(grouped: GroupedCommands): CommandEntry[] {
  const out: CommandEntry[] = [];
  for (const { entries } of grouped) out.push(...entries);
  return out;
}
